import React from 'react';
import styled from 'styled-components';
import HowToRead, { SectionShell, SubHeading, Panel, Muted, EditButton, Grid, Cell, HeadCell } from './HowToRead';
import { formatPct, themeShare, commentScoreRows } from '../reportModel';

const BlockHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 18px;
  padding-bottom: 6px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.report.brand};
  b { color: ${({ theme }) => theme.colors.report.noteTitle}; margin-right: 8px; }
  strong { color: ${({ theme }) => theme.colors.report.brand}; }
`;

const ThemeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 2.4fr;
  gap: 20px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.report.grayLight};
  font-size: 13px;
`;

const ShareBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  div { flex: 1; height: 6px; border-radius: 3px; background: ${({ theme }) => theme.colors.report.grayLight}; }
  div > div { height: 100%; background: ${({ theme }) => theme.colors.report.brand}; }
  span { font-weight: 800; color: ${({ theme }) => theme.colors.report.brand}; }
`;

const SynthesisGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  div { background: ${({ theme }) => theme.colors.report.paper}; border-radius: 6px; padding: 12px 14px; }
  b { display: block; color: ${({ theme }) => theme.colors.report.noteTitle}; margin-bottom: 6px; }
`;

export default function CommentThemes({ data, narrative, t, canEdit, onEdit }) {
  const editable = canEdit && !!narrative;
  return (
    <SectionShell
      id="s05"
      title={t('survey.report.section.s05')}
      actions={editable ? <EditButton t={t} onClick={() => onEdit('themes')} testId="edit-themes" /> : null}
    >
      <HowToRead t={t}>{t('survey.report.help.themes')}</HowToRead>

      {data.freeText.map((ft) => {
        const block = narrative ? (narrative.themes || []).find((x) => x.questionId === ft.questionId) : null;
        return (
          <div key={ft.questionId}>
            <BlockHead>
              <span><b>{ft.code || ''}</b><strong>{ft.text}</strong></span>
              {/* Eşik altı yorum sayısı sunucudan gelmez (0) — yeniden inceleme N7; durum `sufficient`'tan okunur. */}
              {ft.sufficient && <Muted>{t('survey.report.commentsCount', { count: ft.commenters })}</Muted>}
            </BlockHead>
            {!ft.sufficient && <Muted style={{ padding: '10px 0' }}>{t('survey.report.notEnoughComments')}</Muted>}
            {ft.sufficient && block && block.items.map((item) => {
              const share = themeShare(item, block.evaluated);
              return (
                <ThemeRow key={item.id}>
                  <div>
                    <b>{item.name}</b>
                    <ShareBar><div><div style={{ width: `${share}%` }} /></div><span>{formatPct(t, share)}</span></ShareBar>
                  </div>
                  <div>{item.summary}</div>
                </ThemeRow>
              );
            })}
            {ft.sufficient && block && block.sampled && (
              <Muted>{t('survey.report.sampledNote', { count: block.commenters, sample: block.evaluated })}</Muted>
            )}
          </div>
        );
      })}

      {narrative && (
        <>
          <Panel>
            <SubHeading style={{ marginTop: 0 }}>
              {t('survey.report.synthesis')}
              {canEdit && <EditButton t={t} onClick={() => onEdit('synthesis')} testId="edit-synthesis" />}
            </SubHeading>
            <SynthesisGrid>
              {narrative.synthesis.map((s, i) => <div key={i}><b>{String(i + 1).padStart(2, '0')}</b>{s}</div>)}
            </SynthesisGrid>
          </Panel>

          <SubHeading>
            {t('survey.report.commentScore')}
            {canEdit && <EditButton t={t} onClick={() => onEdit('commentScoreLinks')} testId="edit-commentScoreLinks" />}
          </SubHeading>
          <Muted style={{ marginBottom: 8 }}>{t('survey.report.commentScoreHint')}</Muted>
          <Grid $cols="minmax(160px, 1.4fr) minmax(120px, 1fr) 80px minmax(140px, 1fr)">
            <HeadCell>{t('survey.report.colTheme')}</HeadCell>
            <HeadCell>{t('survey.report.colQuestion')}</HeadCell>
            <HeadCell $align="right">{t('survey.report.colFavorable')}</HeadCell>
            <HeadCell>{t('survey.report.colAssessment')}</HeadCell>
            {commentScoreRows(narrative, data).map((r, i) => (
              <React.Fragment key={i}>
                <Cell>{r.themeName}</Cell>
                <Cell>{r.codes.join(' · ')}</Cell>
                <Cell $bold $align="right">{formatPct(t, r.favorablePct)}</Cell>
                <Cell>{t(`survey.report.assessment.${r.assessment}`)}</Cell>
              </React.Fragment>
            ))}
          </Grid>
        </>
      )}
    </SectionShell>
  );
}
